import type { ExportLens, ExportReason } from './types';

export const LENS_META: Record<
  ExportLens,
  { label: string; question: string; description: string }
> = {
  why: {
    label: 'Why do you want to export?',
    question: 'What is actually driving this?',
    description:
      'Here are some of the common reasons NZ businesses pursue exporting. Many are driven by domestic challenges — competitive pressure, board demands, or underperformance at home. Flag which ones resonate, and add your own below.',
  },
  should: {
    label: 'Should you export?',
    question: 'Is the case real — or just exciting?',
    description:
      'These reasons test whether exporting solves a real problem for your NZ business and can actually work from this distance, before you commit resource to it.',
  },
  ready: {
    label: 'Are you ready to export?',
    question: 'Could you execute it well right now?',
    description:
      'These reasons test whether your business can handle the extra complexity — or whether export success would quietly stretch what you have too thin.',
  },
};

/**
 * The core educational content. Each reason is a "reason not to export" drawn
 * from common NZ food & beverage export failure patterns. Written from the
 * perspective of a Kiwi brand weighing up whether to take their product offshore.
 *
 * To weave in more over time: add `resources` here, or pass `reasonResources`
 * / `reasonExtensions` into the component keyed by these ids.
 */
export const EXPORT_REASONS: ExportReason[] = [
  // --- Why do you want to export? ----------------------------------------
  {
    id: 'pushed-out-domestic',
    lens: 'why',
    importance: 'critical',
    title: 'Feeling pushed out of your own market',
    summary:
      "When competition intensifies at home, exporting can feel like an escape route. But if you can't compete here — where you know the customers, retailers, and rules — adding a harder market 12,000 km away rarely improves things.",
    challenge:
      'Are you looking offshore because you genuinely see opportunity there — or because you feel crowded out here?',
  },
  {
    id: 'board-revenue-pressure',
    lens: 'why',
    importance: 'high',
    title: 'Board or advisor pressure to grow revenue',
    summary:
      'External pressure to show aggressive year-on-year growth can make exporting look like the obvious lever. But export revenue is slow, expensive, and uncertain — a poor answer to a boardroom timeline.',
    challenge:
      'Is this export plan driven by a genuine market opportunity — or by a growth target that needs a story behind it?',
  },
  {
    id: 'struggling-domestically',
    lens: 'why',
    importance: 'critical',
    title: 'Not doing well domestically',
    summary:
      "If your brand is struggling to gain traction in NZ, a new market won't magically perform better. The fundamentals that aren't working here — product-market fit, pricing, distribution — will be harder to fix from further away.",
    challenge:
      "Would you describe your NZ business as healthy and growing — or are you hoping a different market solves what isn't working here?",
  },
  {
    id: 'opportunistic-inquiry',
    lens: 'why',
    importance: 'moderate',
    title: 'Someone approached you',
    summary:
      'A distributor expressing interest at a trade show is flattering but not a strategy. Many NZ brands jump at the first offshore interest without asking whether the opportunity is real or just someone filling a catalogue.',
    challenge:
      'Did you pursue this market deliberately — or did someone approach you and it felt too good to say no?',
  },

  // --- Should you export from here? --------------------------------------
  {
    id: 'no-home-success',
    lens: 'should',
    importance: 'critical',
    title: 'No repeatable success at home',
    summary:
      "If you haven't cracked consistent, profitable growth across NZ — Woolworths, New World, food service, or D2C — a new market usually multiplies your problems rather than solving them.",
    challenge:
      "Can you point to repeatable, profitable sell-through in NZ — or are you hoping an overseas market fixes what isn't working here yet?",
  },
  {
    id: 'weak-product-market-fit',
    lens: 'should',
    importance: 'critical',
    title: 'Weak product-market fit abroad',
    summary:
      "What sells in NZ doesn't automatically resonate offshore. Taste profiles, portion sizes, price expectations, and consumption occasions can differ sharply — even in Australia.",
    challenge:
      'What evidence — not assumption — says consumers in this market will choose and repurchase your product at the landed price?',
  },
  {
    id: 'no-advantage',
    lens: 'should',
    importance: 'high',
    title: 'No clear competitive advantage',
    summary:
      '"Made in New Zealand" carries some weight, but it\'s rarely enough on its own. You need a compelling reason for shoppers to choose you over local and global brands already on shelf with lower freight costs.',
    challenge:
      "If the NZ origin story disappeared from your packaging, what would still make a shopper in that market pick you over what's already there?",
  },
  {
    id: 'unit-economics',
    lens: 'should',
    importance: 'critical',
    title: "Unit economics don't survive the distance",
    summary:
      'NZ is far from everywhere. Freight from Auckland to a distribution centre in the US, UK, or Asia is expensive. Add tariffs, distributor margin, retailer margin, in-market promotion, and FX — the maths can quickly turn against you.',
    challenge:
      'Do your margins still work after sea freight, customs duty, distributor cut, retailer margin, required promotional spend, and a bad NZD month?',
  },
  {
    id: 'customer-acquisition-cost',
    lens: 'should',
    importance: 'high',
    title: "Demand won't create itself from 12,000 km away",
    summary:
      "A retail listing is not a sale. You still need to fund awareness, trial, sampling, and marketing in-market — and you can't do it with a quick drive to the store. Most NZ brands underestimate how much it costs to generate pull from the other side of the world.",
    challenge:
      'If the retailer and distributor do zero marketing for you, how would anyone in that market discover your brand?',
  },
  {
    id: 'currency-geopolitical-risk',
    lens: 'should',
    importance: 'moderate',
    title: 'Currency & geopolitical exposure',
    summary:
      "NZ exporters are heavily exposed to FX swings. A 10–15% move in the NZD can wipe out a year of margin. Add shipping disruption, trade policy shifts, or tariff changes — risks most domestic competitors don't face.",
    challenge:
      'What happens to this plan if the NZD strengthens 15% against your target currency, or container rates double again?',
  },
  {
    id: 'opportunity-cost',
    lens: 'should',
    importance: 'high',
    title: 'Opportunity cost of going offshore',
    summary:
      'The same capital, founder time, and team bandwidth might deliver a better return by growing NZ distribution, entering Australia first, improving margins, or launching a new product domestically.',
    challenge:
      'Is exporting genuinely your highest-return move right now — or just the most exciting story to tell your board?',
  },

  // --- Are you ready to export? ------------------------------------------
  {
    id: 'insufficient-capital',
    lens: 'ready',
    importance: 'critical',
    title: 'Insufficient capital & patience',
    summary:
      "Export often takes 2–5 years to pay off for NZ brands. You'll fund product before payment, carry inventory offshore, cover travel, and absorb promotional costs — all while keeping your NZ operation healthy.",
    challenge:
      'Could you fund a reorder before the first shipment has been paid for — and keep doing that for two years without export revenue covering its own costs?',
  },
  {
    id: 'leadership-distraction',
    lens: 'ready',
    importance: 'high',
    title: 'Leadership distraction',
    summary:
      'For most NZ food & beverage companies, the founder or GM is the business. Every week spent on export — travel, calls at odd hours, relationship management — is a week not spent winning at home.',
    challenge:
      'What three important things will leadership stop doing to make genuine room for this market?',
  },
  {
    id: 'production-capacity',
    lens: 'ready',
    importance: 'high',
    title: 'Production capacity constraints',
    summary:
      "Many NZ manufacturers are already at or near capacity. Winning a big export order is a problem if it means short-shipping Foodstuffs or Woolworths NZ. Stock-outs damage relationships you've spent years building.",
    challenge: 'If export demand beat your forecast by 50%, which current NZ customer loses stock?',
  },
  {
    id: 'market-knowledge',
    lens: 'ready',
    importance: 'moderate',
    title: 'Lack of in-market knowledge',
    summary:
      "Regulations, retail structures, distributor networks, consumer preferences, and cultural nuance are expensive to learn by trial and error from NZ. What you don't know will cost you.",
    challenge:
      "What do you actually know about how this market works — versus what you're assuming based on a trade show conversation or a distributor's pitch?",
  },
  {
    id: 'regulatory-complexity',
    lens: 'ready',
    importance: 'moderate',
    title: 'Regulatory & compliance barriers',
    summary:
      "Every market has different rules on ingredients, labelling, nutritional claims, allergen declarations, and import documentation. What's compliant for NZ Food Standards may not pass in the US, EU, or Asia.",
    challenge:
      'Which single compliance requirement could stop your product at the border or force expensive reformulation?',
  },
  {
    id: 'distribution-strategy',
    lens: 'ready',
    importance: 'high',
    title: 'No committed distribution strategy',
    summary:
      '"Find a distributor" is not a strategy. Many NZ brands sign the first partner who shows interest — often someone who adds them to a catalogue of 200 brands and does little active selling.',
    challenge:
      'What will make your brand matter to a distributor six months after the first launch meeting — and how will you hold them accountable?',
  },
  {
    id: 'org-readiness',
    lens: 'ready',
    importance: 'moderate',
    title: 'Organisational readiness',
    summary:
      "Export needs systems: forecasting, export documentation, customer service across time zones, and someone who owns it day-to-day. Enthusiasm and a good product aren't enough.",
    challenge:
      'Who owns this market day-to-day, how much of their week is genuinely protected for it, and what systems are in place to support them?',
  },
];

export const REASONS_BY_LENS: Record<ExportLens, ExportReason[]> = {
  why: EXPORT_REASONS.filter((reason) => reason.lens === 'why'),
  should: EXPORT_REASONS.filter((reason) => reason.lens === 'should'),
  ready: EXPORT_REASONS.filter((reason) => reason.lens === 'ready'),
};

export const LENS_ORDER: ExportLens[] = ['why', 'should', 'ready'];
